import { useState } from "react";
import { useOrganizationSearch } from "@/features/members/hooks/useOrganizations";
import { Input } from "@/components/ui/input";

/**
 * Naming an organization, with the existing ones offered.
 *
 * The directory is only one row per entity if two stewards of the same estate
 * type its name the same way. Matching is case-insensitive server-side, but
 * "Estate of Jack Tworkov" and "Jack Tworkov Estate" are two rows and a forked
 * directory — so this offers what already exists as you type, with the number of
 * stewards, which is the signal that tells you it is the right one.
 *
 * Still free text: a new organization is created by typing a name nobody has
 * used, which is how most people's first organization comes into being.
 */
export function OrganizationPicker({
  value,
  onChange,
  id,
  placeholder = "Entity name",
  className,
}: {
  value: string;
  onChange: (name: string) => void;
  id?: string;
  placeholder?: string;
  className?: string;
}) {
  const [focused, setFocused] = useState(false);
  const { data } = useOrganizationSearch(value.trim());
  const matches = (data?.organizations ?? []).filter(
    (organization) => organization.name.toLowerCase() !== value.trim().toLowerCase()
  );
  return (
    <div className={`relative ${className ?? ""}`}>
      <Input
        id={id}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => setFocused(true)}
        // Blur is delayed so a click on a suggestion lands before the list goes.
        onBlur={() => window.setTimeout(() => setFocused(false), 150)}
        onChange={(e) => onChange(e.target.value)}
      />
      {/* Naming a row somebody else stewards no longer joins it, so the note
          that said "links to the existing entry" was describing something
          that does not happen. The server says what to do instead. */}
      {focused && value.trim().length > 0 && matches.length > 0 && (
        <ul className="bg-popover absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border p-1 shadow-md">
          {matches.slice(0, 8).map((organization) => (
            <li key={organization.id}>
              <button
                type="button"
                className="hover:bg-accent flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(organization.name);
                  setFocused(false);
                }}
              >
                <span className="truncate">{organization.name}</span>
                <span className="text-muted-foreground shrink-0 text-xs">
                  {organization.stewards}{" "}
                  {organization.stewards === 1 ? "steward" : "stewards"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default OrganizationPicker;
